import React from 'react';
import { Wrench, ShieldCheck, Zap, ArrowRight, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import LazyVideo from '../common/LazyVideo';
import { VIDEO_CDN } from '../../content/videos';
import { MAINTENANCE_SERVICES, DELIVERY_ZONES, formatRD, serviceWhatsAppLink } from '../../content/maintenanceServices';

const ServiceMarketing = () => {
    return (
        <section id="mantenimiento" className="relative bg-black py-24 lg:py-32 overflow-hidden">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0 opacity-40">
                <img
                    src="https://images.unsplash.com/photo-1571068316344-75bc76f77890?q=80&w=1920&auto=format&fit=crop"
                    alt="PROOMTB Professional Workshop"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
            </div>

            <div className="relative max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 flex flex-col lg:flex-row items-center gap-16">

                {/* Text Content */}
                <div className="lg:w-1/2 text-white">
                    <span className="inline-block py-1 px-3 border border-white/30 text-xs font-bold uppercase tracking-[0.2em] mb-6 backdrop-blur-sm">
                        Servicio Técnico Elite
                    </span>
                    <h2 className="text-5xl md:text-7xl font-black uppercase italic leading-none mb-8 tracking-tight">
                        Taller <br />
                        <span className="gradient-text">
                            Profesional
                        </span>
                    </h2>

                    <p className="text-lg md:text-xl text-gray-300 mb-12 max-w-xl font-light leading-relaxed">
                        En PROOMTB & ROAD, no solo vendemos bicicletas; garantizamos su rendimiento eterno.
                        Nuestro taller especializado cuenta con mecánicos certificados y herramientas de precisión para mantener tu bicicleta como el primer día.
                    </p>

                    <div className="space-y-8 mb-10">
                        {/* Packages, cheapest first */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {MAINTENANCE_SERVICES.map((service) => (
                                <a
                                    key={service.id}
                                    href={serviceWhatsAppLink(service)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`block border p-6 backdrop-blur-sm rounded-sm relative overflow-hidden group transition-all cursor-pointer ${service.recommended ? 'border-white/40 bg-white/10 hover:bg-white/15' : 'border-white/20 bg-white/5 hover:border-white/40 hover:bg-white/10'}`}
                                >
                                    {service.recommended && (
                                        <div className="absolute top-0 right-0 bg-white text-black text-[10px] font-bold px-2 py-1 uppercase">Recomendado</div>
                                    )}
                                    <div className="flex justify-between items-start">
                                        <h3 className="text-lg font-black italic uppercase tracking-tight mb-1 text-white group-hover:text-raymon-blue transition-colors">{service.name}</h3>
                                        <ArrowRight className="w-5 h-5 shrink-0 text-gray-500 group-hover:text-white transition-colors opacity-0 group-hover:opacity-100" />
                                    </div>
                                    <div className="text-2xl font-black text-white mb-4">{formatRD(service.price)}</div>
                                    <ul className="text-sm text-gray-300 space-y-2">
                                        {service.includes.map((item) => (
                                            <li key={item} className="flex items-center gap-2"><div className="w-1 h-1 bg-white rounded-full"></div> {item}</li>
                                        ))}
                                        {service.note && (
                                            <li className="flex items-center gap-2 font-bold text-white"><div className="w-1 h-1 bg-white rounded-full"></div> {service.note}</li>
                                        )}
                                    </ul>
                                </a>
                            ))}
                        </div>

                        {/* Additional Services & Delivery */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 text-sm pt-4 border-t border-white/10">
                            <div>
                                <h4 className="font-bold text-white uppercase mb-4 flex items-center gap-2">
                                    <Wrench className="w-4 h-4 text-raymon-blue" /> Servicios Adicionales
                                </h4>
                                <ul className="text-gray-400 space-y-2">
                                    <li>Sangrado de frenos</li>
                                    <li>Mantenimiento de suspensión</li>
                                    <li>Mantenimiento de catre</li>
                                    <li>Nivelado de aros</li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-bold text-white uppercase mb-4 flex items-center gap-2">
                                    <Truck className="w-4 h-4 text-raymon-blue" /> Servicio a Domicilio
                                </h4>
                                <div className="space-y-3 text-gray-400">
                                    {DELIVERY_ZONES.map(({ zone, price }) => (
                                        <div key={zone} className="flex justify-between items-center gap-4 border-b border-white/5 pb-2">
                                            <span>{zone}</span>
                                            <span className="text-white font-bold bg-white/10 px-2 py-0.5 rounded whitespace-nowrap">{price}</span>
                                        </div>
                                    ))}
                                    <p className="text-[10px] italic text-gray-500 mt-2">*Buscar y llevar</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Link
                        to="/agendar-mantenimiento"
                        className="inline-flex items-center space-x-4 bg-white text-black px-10 py-5 text-sm font-bold uppercase tracking-widest hover:bg-gray-200 transition-all group"
                    >
                        <span>Agendar Mantenimiento</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                    </Link>
                </div>

                {/* Optional: Second Visual Element for Desktop */}
                <div className="hidden lg:block lg:w-1/2 relative">
                    <div className="relative aspect-square border-2 border-white/10 rounded-3xl p-12 overflow-hidden">
                        <LazyVideo
                            src={`${VIDEO_CDN}/taller.mp4`}
                            poster={`${VIDEO_CDN}/taller.jpg`}
                            className="w-full h-full object-cover rounded-2xl transition-all duration-700 hover:scale-105"
                        />
                        <div className="absolute bottom-16 right-16 bg-white p-8 rounded-2xl shadow-2xl">
                            <div className="text-black font-black text-4xl leading-tight">100%<br />EXPERTOS</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ServiceMarketing;
